import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Link,
  Card,
  CardContent
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  resetForgotPasswordState,
  resetVerifyEmailState,
  verifyEmailRequest
} from "../actions/authActions";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useForm, Controller } from "react-hook-form";
import AirplanemodeActiveIcon from "@mui/icons-material/AirplanemodeActive";
import AtithiLogo from "../../../assets/Atithiseva-Logo-Final-1024x298.png";
import { useLocation, useNavigate } from "react-router-dom";
import "../../../styles/VerifyEmail.css";
import {
  FORGOT_PASSWORD,
  RESET_PASSWORD
} from "../../../component/common/Routes/AppRoutes";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CustomButton from "../../../component/ui/FormFields/Button";

export default function VerifyEmail() {
  const { control, handleSubmit, clearErrors, watch, setValue } = useForm({
    defaultValues: {
      code: ["", "", "", "", "", ""]
    }
  });
  const location = useLocation();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const email = location.state?.email;

  const { loading, success, error } = useSelector(
    (state) => state.verifyEmailReducer
  );
  const [timer, setTimer] = useState(60);
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const [timerId, setTimerId] = useState(null);

  useEffect(() => {
    if (timer > 0) {
      const id = setTimeout(() => setTimer(timer - 1), 1000);
      setTimerId(id);
      return () => clearTimeout(id);
    } else {
      setIsResendDisabled(false);
    }
  }, [timer]);

  const stopTimer = () => {
    if (timerId) clearTimeout(timerId);
    setTimer(0);
    setIsResendDisabled(false);
  };

  const onSubmitForm = (data) => {
    const enteredCode = data.code.join("");
    dispatch(verifyEmailRequest({ email, verification_code: enteredCode }));
  };

  useEffect(() => {
    if (success) {
      toast.success("OTP Verified Successfully", {
        position: "top-center",
        autoClose: 3000,
        onClose: () => {
          dispatch(resetVerifyEmailState());
          navigate(RESET_PASSWORD, { state: { email } });
          stopTimer();
        }
      });
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      toast.error("Enter Correct OTP", {
        position: "top-center",
        autoClose: 3000
      });
      dispatch(resetVerifyEmailState());
    }
  }, [error]);
  useEffect(() => {
    dispatch(resetVerifyEmailState());
  }, []);

  const handleInputChange = (e, index, field) => {
    const updatedCode = [...field.value];
    const value = e.target.value;
    const isBackspace = e.nativeEvent.inputType === "deleteContentBackward";

    if (isBackspace) {
      if (index > 0 && !updatedCode[index]) {
        updatedCode[index - 1] = "";
        document.getElementById(`code-${index - 1}`).focus();
      } else {
        updatedCode[index] = "";
      }
    } else if (value.match(/^[0-9]$/)) {
      updatedCode[index] = value;
      if (index < updatedCode.length - 1) {
        document.getElementById(`code-${index + 1}`).focus();
      }
    }

    field.onChange(updatedCode);

    if (field.error) clearErrors("code");
  };

  const handleResendCode = () => {
    dispatch({
      type: "FORGOT_PASSWORD_REQUEST",
      payload: { email: email }
    });
    toast.success("A new verification code has been sent to your email.");
    setTimer(60);
    setIsResendDisabled(true);
  };

  return (
    <>
      <ToastContainer position="top-center" />
      <Box className="verify-email-container">
        <Box className="airplane-icon-position">
          <AirplanemodeActiveIcon
            sx={{
              fontSize: { xs: 100, sm: 150, md: 200 },
              color: "#C62828",
              opacity: 0.1
            }}
          />
        </Box>

        <Card
          sx={{
            maxWidth: { xs: 320, sm: 400 },
            width: "100%",
            borderRadius: 4,
            boxShadow: 3,
            zIndex: 2,
            padding: { xs: 3, sm: 5 }
          }}
        >
          <CardContent>
            <Box sx={{ textAlign: "center" }}>
              <img
                src={AtithiLogo}
                alt="Atithiseva Logo"
                style={{ maxWidth: "150px", marginBottom: "10px" }}
              />
            </Box>
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ color: "#E31E24", fontSize: { xs: "18px", sm: "20px" } }}
              >
                Verify Your Email Address
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#757575",
                  mt: 1,
                  fontSize: { xs: "12px", sm: "14px" }
                }}
              >
                Enter the 6-digit verification code sent to your email address.
              </Typography>

              {/* {error && (
                <Typography
                  variant='body2'
                  color='error'
                  sx={{ textAlign: 'center', mb: 2 }}
                >
                  {error}
                </Typography>
              )} */}

              {success && (
                <Typography
                  variant="body2"
                  color="success"
                  sx={{ textAlign: "center", mb: 2 }}
                >
                  Email verified successfully!
                </Typography>
              )}
            </Box>

            <form onSubmit={handleSubmit(onSubmitForm)}>
              <Controller
                name="code"
                control={control}
                rules={{
                  validate: (value) =>
                    value.join("").length === 6 || "Please fill all fields"
                }}
                render={({ field, fieldState }) => (
                  <>
                    <Grid
                      container
                      spacing={5}
                      justifyContent="center"
                      sx={{ mb: 2 }}
                    >
                      {field.value.map((digit, index) => (
                        <Grid item xs={2} key={index}>
                          <TextField
                            id={`code-${index}`}
                            value={digit}
                            onChange={(e) => handleInputChange(e, index, field)}
                            onKeyDown={(e) => {
                              if (
                                e.key === "Backspace" &&
                                !digit &&
                                index > 0
                              ) {
                                document
                                  .getElementById(`code-${index - 1}`)
                                  .focus();
                              }
                            }}
                            variant="outlined"
                            inputProps={{
                              maxLength: 1,
                              style: { textAlign: "center", fontSize: "18px" }
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                height: { xs: "40px", sm: "40px" },
                                width: { xs: "40px", sm: "40px" }
                              }
                            }}
                            error={!!fieldState.error}
                          />
                        </Grid>
                      ))}
                    </Grid>

                    {fieldState.error && (
                      <Typography
                        variant="body2"
                        color="error"
                        sx={{ textAlign: "center", mb: 2 }}
                      >
                        {fieldState.error.message}
                      </Typography>
                    )}
                  </>
                )}
              />

              {/* <Button
                variant='contained'
                type='submit'
                fullWidth
                size='large'
                className='verify-email-btn'
              >
                Verify Email
              </Button> */}
              <CustomButton
                variant="contained"
                className="verify-email-btn"
                type="submit"
                size="large"
                fullwidth={true}
                loading={loading}
                text={"  Verify Email"}
              />

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  textAlign: "center",
                  mt: 2,
                  fontSize: { xs: "12px", sm: "14px" }
                }}
              >
                <button
                  style={{
                    fontWeight: "bold",
                    color: isResendDisabled ? "#9e9e9e" : "#E31E24",
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: isResendDisabled ? "default" : "pointer",
                    fontSize: "14px"
                  }}
                  onClick={handleResendCode}
                  disabled={isResendDisabled}
                >
                  {isResendDisabled
                    ? `Resend Code in ${timer}s`
                    : "Resend Code"}
                </button>
              </Typography>

              <Box sx={{ textAlign: "center", mt: 2 }}>
                <Button
                  variant="contained"
                  className="back-button"
                  onClick={() => {
                    dispatch(resetForgotPasswordState());
                    navigate(FORGOT_PASSWORD);
                  }}
                >
                  <ArrowBackIcon />
                </Button>
              </Box>
            </form>
          </CardContent>
        </Card>
      </Box>
    </>
  );
}
